<template>
  <div class="rich-text-editor" :style="{ '--editor-height': height }">
    <div class="editor-toolbar-wrap">
      <Toolbar
        class="editor-toolbar"
        :editor="editor"
        :default-config="toolbarConfig"
        :mode="mode"
      />
    </div>
    <Editor
      class="editor-body"
      :value="value"
      :default-config="editorConfig"
      :mode="mode"
      @input="handleInput"
      @onCreated="handleCreated"
    />
  </div>
</template>

<script>
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import '@wangeditor/editor/dist/css/style.css'
import {
  ATTACHMENT_MENU_KEY,
  initWangEditorAttachmentMenu,
  setEditorAttachmentHandler
} from '@/components/admin/setupWangEditor'
import {
  EDITOR_LIMITS,
  escapeHtml,
  uploadEditorAsset
} from '@/utils/editorUpload'

initWangEditorAttachmentMenu()

function isValidUrl(url) {
  if (!url) return false
  // 允许相对路径和 http/https
  if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) return true
  try {
    const u = new URL(url)
    return ['http:', 'https:'].includes(u.protocol)
  } catch (err) {
    return false
  }
}

function escapeHtmlAttr(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export default {
  name: 'RichTextEditor',
  components: { Editor, Toolbar },
  props: {
    value: {
      type: String,
      default: ''
    },
    bizType: {
      type: String,
      default: 'cms'
    },
    height: {
      type: String,
      default: '420px'
    },
    placeholder: {
      type: String,
      default: '请输入正文内容，支持图文、视频与附件'
    },
    mode: {
      type: String,
      default: 'default'
    }
  },
  data() {
    return {
      editor: null,
      toolbarConfig: {
        insertKeys: {
          index: 22,
          keys: [ATTACHMENT_MENU_KEY]
        }
      },
      editorConfig: {
        placeholder: this.placeholder,
        MENU_CONF: {
          uploadImage: {
            allowedFileTypes: ['image/*'],
            customUpload: (file, insertFn) => this.uploadImage(file, insertFn)
          },
          uploadVideo: {
            allowedFileTypes: ['video/*'],
            customUpload: (file, insertFn) => this.uploadVideo(file, insertFn)
          }
        }
      }
    }
  },
  created() {
    this._attachmentHandler = editor => this.handleAttachmentUpload(editor)
    setEditorAttachmentHandler(this._attachmentHandler)
  },
  beforeDestroy() {
    if (this._attachmentHandler) {
      setEditorAttachmentHandler(null)
    }
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  },
  methods: {
    handleCreated(editor) {
      this.editor = Object.seal(editor)
    },
    handleInput(html) {
      this.$emit('input', html)
    },
    async uploadImage(file, insertFn) {
      try {
        const { url } = await uploadEditorAsset(file, this.bizType, {
          maxSize: EDITOR_LIMITS.image,
          validateType: item => item.type.startsWith('image/')
        })
        insertFn(url, file.name, url)
      } catch (e) {
        this.$message.error(e.message || '图片上传失败')
      }
    },
    async uploadVideo(file, insertFn) {
      try {
        const { url } = await uploadEditorAsset(file, this.bizType, {
          maxSize: EDITOR_LIMITS.video,
          validateType: item => item.type.startsWith('video/')
        })
        insertFn(url, '')
      } catch (e) {
        this.$message.error(e.message || '视频上传失败')
      }
    },
    handleAttachmentUpload(editor) {
      const input = document.createElement('input')
      input.type = 'file'
      input.style.display = 'none'
      input.onchange = async () => {
        const file = input.files && input.files[0]
        document.body.removeChild(input)
        if (!file) return
        try {
          const { url, fileName } = await uploadEditorAsset(file, this.bizType, {
            maxSize: EDITOR_LIMITS.attachment
          })
          if (!isValidUrl(url)) {
            this.$message.error('附件地址不合法')
            return
          }
          const safeName = escapeHtml(fileName)
          const safeUrl = escapeHtmlAttr(url)
          editor.focus()
          editor.dangerouslyInsertHtml(
            `<p><a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeName}</a></p>`
          )
        } catch (e) {
          this.$message.error(e.message || '附件上传失败')
        }
      }
      document.body.appendChild(input)
      input.click()
    }
  }
}
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  line-height: normal;
}
.editor-toolbar-wrap {
  border-bottom: 1px solid #dcdfe6;
}
.editor-toolbar {
  background: #fafafa;
}
.editor-body {
  height: var(--editor-height);
  overflow-y: hidden;
}
</style>

<style>
.rich-text-editor .w-e-text-container [data-slate-editor] {
  min-height: calc(var(--editor-height) - 42px);
}
.rich-text-editor .w-e-text-container img,
.rich-text-editor .w-e-text-container video {
  display: block;
  width: 100% !important;
  max-width: 100%;
  height: auto !important;
  margin: 12px auto;
}
.rich-text-editor .w-e-text-container [data-w-e-type='video'],
.rich-text-editor .w-e-text-container [data-w-e-type='image'] {
  width: 100%;
}
</style>
